import { Add as AddIcon, CallSplit, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { deleteCompany, getCompanies } from '../../http/CompaniesApi';
import AssetIcon from '../shared/AssetIcon';
import CompanyMetricsHistoryComponent from './CompanyMetricsHistoryComponent';
import AddCompanyDialog from './dialogs/AddCompanyDialog';
import AddStockSplitDialog from './dialogs/AddStockSplitDialog';
import { toAverageOrNull, toNumberOrNull } from './metricsHistory';
import {
  compareToAverage,
  formatRatio,
  formatRatioPercent,
  getRatioSortComparator,
  NO_DATA,
} from './ratios';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const COLORS = {
  BACKGROUND: '#f5f5f5',
  SURFACE: '#ffffff',
  BORDER: '#e0e0e0',
  UP: '#4caf50',
  DOWN: '#f44336',
};

const DEFAULT_ERROR_MESSAGE = 'An error happened!';
const COMPARISON_ICONS = {
  doubleUp: '🥦🥦',
  up: '🥦',
  down: '🍅',
  doubleDown: '🍅🍅',
};
const SHORT_THRESHOLD = 0.025;

export default function CompaniesComponent() {
  const [companies, setCompanies] = useState([]);
  const [snackState, setSnackState] = useState({
    open: false,
    message: DEFAULT_ERROR_MESSAGE,
  });
  const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [addStockSplitDialogOpen, setAddStockSplitDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });
  const [detailCompanyId, setDetailCompanyId] = useState(null);
  const metricsHistoryCache = useRef(new Map());
  const detailRef = useRef(null);
  const detailCompany = companies.find((c) => c.id === detailCompanyId);

  const handleSnackStateChange = useCallback((key, value) => {
    setSnackState((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    async function getData() {
      const { data } = await getCompanies();
      setCompanies(data);
    }

    getData();
  }, []);

  const renderValue = useCallback((average, value) => {
    if (toNumberOrNull(value) === null) {
      return <Typography variant="body">{NO_DATA}</Typography>;
    }
    const comparison = compareToAverage(value, average);
    return (
      <Typography variant="body">
        {formatRatio(value)} ({formatRatio(toAverageOrNull(average))})
        {COMPARISON_ICONS[comparison] ?? ''}
      </Typography>
    );
  }, []);

  const renderPercent = useCallback((average, value) => {
    if (toNumberOrNull(value) === null) {
      return <Typography variant="body">{NO_DATA}</Typography>;
    }
    return (
      <Typography variant="body">
        {formatRatioPercent(value)} (
        {formatRatioPercent(toAverageOrNull(average))})
      </Typography>
    );
  }, []);

  const deleteRow = useCallback(
    async (id) => {
      try {
        await deleteCompany(id);
        setCompanies((prevCompanies) =>
          prevCompanies.filter((r) => r.id !== id),
        );
        setDetailCompanyId((openId) => (openId === id ? null : openId));
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    },
    [handleSnackStateChange],
  );

  const openStockSplitDialog = useCallback((company) => {
    setSelectedCompany(company);
    setAddStockSplitDialogOpen(true);
  }, []);

  const handleStockSplitSuccess = useCallback(async () => {
    // Refresh companies list after stock split
    try {
      const { data } = await getCompanies();
      setCompanies(data);
    } catch (err) {
      handleSnackStateChange(
        'message',
        get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
      );
      handleSnackStateChange('open', true);
    }
  }, [handleSnackStateChange]);

  const buildGrid = useMemo(() => {
    return [
      {
        field: 'timestamp',
        headerName: 'Last update',
        flex: 1,
        valueGetter: (_, row) =>
          row?.state?.timestamp
            ? format(new Date(row.state.timestamp), 'dd/MM p')
            : '',
      },
      {
        field: 'symbol',
        headerName: 'Symbol',
        flex: 1,
        renderCell: ({ formattedValue, row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssetIcon
              iconUrl={row.iconUrl}
              label={row.symbol || row.name}
              variant="rounded"
              size={24}
            />
            {!isMobile && (
              <Typography variant="body" fontWeight="bold">
                {formattedValue}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'name',
        headerName: 'Company',
        flex: 2,
      },
      {
        field: 'price',
        type: 'number',
        valueGetter: (_, row) => row?.state?.price,
        headerName: 'Price',
        flex: 1,
      },
      {
        field: 'currency',
        valueGetter: (_, row) => row?.state?.currency,
        headerName: 'Currency',
        flex: 0.5,
      },
      {
        field: 'EV/REV',
        headerName: 'EV/REV',
        type: 'number',
        flex: 1,
        valueGetter: (_, row) => row?.state?.enterpriseToRevenue,
        getSortComparator: getRatioSortComparator,
        renderCell: ({ row }) => {
          const value = row?.state?.enterpriseToRevenue;
          const metric = row.metrics?.avgEnterpriseToRevenue;
          return renderValue(metric, value);
        },
      },
      {
        field: 'EV/EBITDA',
        headerName: 'EV/EBITDA',
        type: 'number',
        flex: 1,
        valueGetter: (_, row) => row?.state?.enterpriseToEbitda,
        getSortComparator: getRatioSortComparator,
        renderCell: ({ row }) => {
          const value = row?.state?.enterpriseToEbitda;
          const metric = row.metrics?.avgEnterpriseToEbitda;
          return renderValue(metric, value);
        },
      },
      {
        field: 'forwardPE',
        headerName: 'Forward PE',
        type: 'number',
        flex: 1,
        valueGetter: (_, row) => row?.state?.forwardPE,
        getSortComparator: getRatioSortComparator,
        renderCell: ({ row }) => {
          const value = row?.state?.forwardPE;
          const metric = row.metrics?.avgForwardPE;
          return renderValue(metric, value);
        },
      },
      {
        field: 'profitMargins',
        headerName: 'Profit Margins',
        type: 'number',
        flex: 1,
        valueGetter: (_, row) => row?.state?.profitMargins,
        getSortComparator: getRatioSortComparator,
        renderCell: ({ row }) => {
          const value = row?.state?.profitMargins;
          const metric = row.metrics?.avgProfitMargins;
          return renderPercent(metric, value);
        },
      },
      {
        field: 'Short %',
        headerName: 'Short %',
        type: 'number',
        flex: 1,
        valueGetter: (_, row) => row?.state?.shortPercentOfFloat,
        getSortComparator: getRatioSortComparator,
        renderCell: ({ row }) => {
          const value = toNumberOrNull(row?.state?.shortPercentOfFloat);
          if (value === null) {
            return <Typography variant="body">{NO_DATA}</Typography>;
          }
          const fontWeight = value >= SHORT_THRESHOLD ? 'bold' : 'medium';
          return (
            <Typography variant="body" fontWeight={fontWeight}>
              {(value * 100).toFixed(2)}%
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        renderCell: (data) => {
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  openStockSplitDialog(data.row);
                }}
                size="small"
                title="Add stock split"
              >
                <CallSplit />
              </IconButton>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  deleteRow(data.id);
                }}
                size="small"
                title="Delete company"
              >
                <DeleteOutline />
              </IconButton>
            </Box>
          );
        },
      },
    ];
  }, [renderValue, renderPercent, deleteRow, openStockSplitDialog]);

  const toggleDetail = useCallback(({ id }) => {
    setDetailCompanyId((openId) => (openId === id ? null : id));
  }, []);

  useEffect(() => {
    if (detailCompanyId !== null) {
      detailRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [detailCompanyId]);

  return (
    <Box
      sx={{
        p: SPACING.LG,
        px: isMobile ? 0 : SPACING.LG,
        backgroundColor: COLORS.BACKGROUND,
        minHeight: '100vh',
        borderRadius: '8px',
      }}
    >
      <Grid padding={SPACING.SM}>
        <Box
          sx={{
            backgroundColor: COLORS.SURFACE,
            borderRadius: '8px',
            border: `1px solid ${COLORS.BORDER}`,
            overflow: 'hidden',
          }}
        >
          <DataGrid
            autoHeight
            rows={companies}
            columns={buildGrid}
            columnVisibilityModel={{
              name: !isMobile,
              currency: !isMobile,
              actions: !isMobile,
            }}
            density="compact"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20, 50]}
            onRowClick={toggleDetail}
            rowSelectionModel={
              detailCompanyId !== null ? [detailCompanyId] : []
            }
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f9f9f9',
                },
              },
            }}
          />
        </Box>
      </Grid>
      {detailCompany && (
        <Box
          ref={detailRef}
          sx={{ px: isMobile ? 0 : SPACING.SM, scrollMarginTop: 16 }}
        >
          <CompanyMetricsHistoryComponent
            company={detailCompany}
            cache={metricsHistoryCache.current}
            onClose={() => setDetailCompanyId(null)}
          />
        </Box>
      )}
      <Box
        sx={{
          p: SPACING.SM,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: SPACING.SM,
          mt: SPACING.SM,
        }}
      >
        <Button
          onClick={() => setAddCompanyDialogOpen(true)}
          variant="outlined"
          endIcon={<AddIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add company
        </Button>
      </Box>
      <AddCompanyDialog
        setCompanies={setCompanies}
        companies={companies}
        addCompanyDialogOpen={addCompanyDialogOpen}
        setAddCompanyDialogOpen={setAddCompanyDialogOpen}
        setErrMessage={(msg) => handleSnackStateChange('message', msg)}
        errMessage={snackState.message}
        setSnackOpen={(open) => handleSnackStateChange('open', open)}
      />
      {selectedCompany && (
        <AddStockSplitDialog
          companyId={selectedCompany.id}
          companyName={selectedCompany.name}
          addStockSplitDialogOpen={addStockSplitDialogOpen}
          setAddStockSplitDialogOpen={setAddStockSplitDialogOpen}
          setErrMessage={(msg) => handleSnackStateChange('message', msg)}
          errMessage={snackState.message}
          setSnackOpen={(open) => handleSnackStateChange('open', open)}
          onSuccess={handleStockSplitSuccess}
        />
      )}
      <Snackbar
        open={snackState.open}
        autoHideDuration={6000}
        onClose={() => handleSnackStateChange('open', false)}
      >
        <Alert severity="error">{snackState.message}</Alert>
      </Snackbar>
    </Box>
  );
}
